/**
 * The two things this codebase needs from a request layer: a JSON request, and
 * a rejection whose `.response.data` carries the server's own error payload.
 * Interceptors, transforms, cancellation and adapters were never used by any
 * provider, so there is nothing here a library would add.
 *
 * ── Why it is hand-rolled ──
 *
 * One less dependency on the path every request in the game takes. `fetch` is
 * native, cannot be swapped under us, and needs no supply chain to be trusted.
 *
 * The genuinely dangerous version of this was worse than a dependency: every
 * provider's spin WORKER pulled its HTTP library from a public CDN at runtime,
 * on an UNPINNED url, which meant a blocked CDN stopped the game from spinning
 * at all — the worker died on its first line, before `onmessage` existed, so
 * the message it was sent was never answered by anything — and it let a third
 * party change this game's code without us. Both are gone. Do not reintroduce
 * a runtime download, and do not add a package for this.
 *
 * ── The contract ──
 *
 * Resolves `{ data }`, and throws an Error carrying `.response = { status,
 * data }`. That shape is deliberate: it is what the call sites were already
 * written against, so every `response.data` and every
 * `error.response.data.errors` kept working word for word when the request
 * layer changed underneath them. Nothing about how this game reads a server
 * error could drift while that happened.
 *
 * @param   {string} url
 * @param   {object} [init]
 * @param   {string} [init.method]  Defaults to GET.
 * @param   {object} [init.headers]
 * @param   {object} [init.body]    Serialised as JSON when present.
 * @returns {Promise<{data: any}>}
 * @throws  {Error} with `.response = {status, data}` on any non-2xx.
 */
export const requestJson = async (
  url,
  { method = "GET", headers = {}, body } = {},
) => {
  const response = await fetch(url, {
    method,
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  // Parsed BEFORE the status is judged: an error response carries the codes the
  // callers turn into popups, and throwing on the status alone would bin them.
  // A body that is not JSON at all is not an error by itself — a 200 with an
  // empty body is a shape some of these endpoints do return.
  let data = null;
  try {
    data = await response.json();
  } catch {
    data = null;
  }

  if (!response.ok) {
    const error = new Error(
      `HTTP ${response.status}${response.statusText ? ` ${response.statusText}` : ""}`,
    );
    error.response = { status: response.status, data };
    throw error;
  }

  return { data };
};

/**
 * A `get(url, config)` / `post(url, body, config)` facade over `requestJson`,
 * for the providers whose call sites were written against that signature.
 *
 * Kept rather than rewriting those thirteen call sites by hand: the shapes they
 * pass in and read back are identical, so swapping the import is the entire
 * change and there is nowhere for a transcription slip to hide.
 *
 * `Content-Type: application/json` is defaulted on POST because the old layer
 * set it automatically for object bodies, and two providers relied on that by
 * passing no config at all.
 */
export const http = {
  get: (url, config = {}) => requestJson(url, { headers: config.headers }),
  post: (url, body, config = {}) =>
    requestJson(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(config.headers || {}) },
      body,
    }),
};

export default requestJson;
